import { createFileRoute, Link } from "@tanstack/react-router";
import { GroupCoffeeCard } from "@/components/GroupCoffeeCard";
import { CoffeeFollowUpPrompt } from "@/components/CoffeeFollowUpPrompt";
import { DistrictHintBanner } from "@/components/DistrictHintBanner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { Coffee } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import {
  ContactButtons,
  ContactSaver,
  hasAnyContact,
  MY_CONTACT_KEY,
  useMyContact,
} from "@/lib/coffee-contact";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

export const Route = createFileRoute("/_authenticated/coffee")({
  head: () => ({
    meta: [
      { title: "Кофе — TurkeyDrive" },
      { name: "description", content: "Приглашения на кофе: входящие и отправленные, ответы и контакты для встречи." },
      { property: "og:title", content: "Кофе — TurkeyDrive" },
      { property: "og:description", content: "Приглашения на кофе: входящие и отправленные, ответы и контакты для встречи." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: CoffeePage,
});

export const COFFEE_INVITES_KEY = ["coffee-invites", "mine"] as const;

type Invite = {
  id: string;
  direction: string;
  other_user_id: string;
  full_name: string;
  username: string;
  photo_url: string | null;
  city: string | null;
  status: string;
  message: string | null;
  reply_message: string | null;
  created_at: string;
  responded_at: string | null;
  confirmed_at: string | null;
  contact_telegram: string | null;
};

const STATUS_LABEL: Record<string, string> = {
  sent: "Ждём ответа",
  accepted: "Договорились",
  declined: "В этот раз не получилось",
  met: "Встреча состоялась",
  not_met: "Встреча не состоялась",
};

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString("ru-RU", { day: "numeric", month: "long" });
}

function CoffeePage() {
  const { user } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: COFFEE_INVITES_KEY,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("my_coffee_invites");
      if (error) throw error;
      return (data ?? []) as Invite[];
    },
  });
  const myContact = useMyContact(user?.id);
  const incoming = (data ?? []).filter((i) => i.direction === "incoming");
  const outgoing = (data ?? []).filter((i) => i.direction === "outgoing");

  // Соцссылки партнёров по согласованным встречам — одним запросом на всех.
  const agreedIds = (data ?? [])
    .filter((i) => i.status === "accepted" || i.status === "met")
    .map((i) => i.other_user_id);
  const { data: partnerContacts = {} } = useQuery({
    queryKey: ["coffee-partner-contacts", agreedIds.join(",")],
    enabled: agreedIds.length > 0,
    queryFn: async () => {
      const { data: rows, error } = await supabase
        .from("profiles")
        .select("user_id, telegram, social_links")
        .in("user_id", agreedIds);
      if (error) throw error;
      const map: Record<string, { telegram: string | null; social_links: string[] }> = {};
      for (const p of rows ?? []) {
        map[p.user_id] = {
          telegram: (p.telegram as string | null) ?? null,
          social_links: ((p.social_links as string[] | null) ?? []).filter(Boolean),
        };
      }
      return map;
    },
  });

  return (
    <main className="mx-auto max-w-2xl px-4 py-6">
      <h1 className="mb-5 text-2xl font-bold text-td-dark">Кофе</h1>
      <CoffeeFollowUpPrompt />
      <GroupCoffeeCard />
      <DistrictHintBanner className="mb-5" />
      {isLoading ? (
        <p className="text-sm text-td-muted">Загружаем…</p>
      ) : !data?.length ? (
        <div className="rounded-2xl border border-td-line bg-white p-6 text-center text-sm text-td-muted">
          <Coffee className="mx-auto mb-2 h-8 w-8 text-td-teal" />
          Приглашений пока нет. Загляни в раздел{" "}
          <Link to="/people" className="text-td-teal hover:underline">«Люди»</Link> — там видно, кто открыт к знакомствам.
        </div>
      ) : (
        <div className="space-y-8">
          {incoming.length > 0 && (
            <section>
              <h2 className="mb-3 text-lg font-semibold text-td-dark">Тебя приглашают</h2>
              <div className="space-y-3">{incoming.map((i) => <InviteCard key={i.id} inv={i} partner={partnerContacts[i.other_user_id]} myContact={myContact.data} myUserId={user?.id} />)}</div>
            </section>
          )}
          {outgoing.length > 0 && (
            <section>
              <h2 className="mb-3 text-lg font-semibold text-td-dark">Твои приглашения</h2>
              <div className="space-y-3">{outgoing.map((i) => <InviteCard key={i.id} inv={i} partner={partnerContacts[i.other_user_id]} myContact={myContact.data} myUserId={user?.id} />)}</div>
            </section>
          )}
        </div>
      )}
    </main>
  );
}

function InviteCard({
  inv,
  partner,
  myContact,
  myUserId,
}: {
  inv: Invite;
  partner?: { telegram: string | null; social_links: string[] };
  myContact?: { telegram: string | null; social_links: string[] };
  myUserId?: string;
}) {
  const qc = useQueryClient();
  const [choice, setChoice] = useState<boolean | null>(null);
  const [reply, setReply] = useState("");
  const canRespond = inv.direction === "incoming" && !inv.responded_at;

  const respond = useMutation({
    mutationFn: async (accept: boolean) => {
      const { error } = await supabase.rpc("respond_to_coffee_invite", {
        p_id: inv.id,
        p_accept: accept,
        p_message: reply.trim() || undefined,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setChoice(null);
      qc.invalidateQueries({ queryKey: ["coffee-invites"] });
      qc.invalidateQueries({ queryKey: ["notifications"] });
    },
    onError: (e: Error & { hint?: string }) => {
      if (e.hint === "no_contact") {
        toast.error("Сначала добавь контакт для связи");
        qc.invalidateQueries({ queryKey: MY_CONTACT_KEY });
      } else {
        toast.error(e.message || "Не удалось отправить ответ");
      }
    },
  });

  const agreed = inv.status === "accepted" || inv.status === "met";
  const partnerTelegram = partner?.telegram ?? inv.contact_telegram;
  const partnerLinks = partner?.social_links ?? [];
  const partnerHasContact = !!partnerTelegram?.trim() || partnerLinks.length > 0;
  const meHasContact = hasAnyContact(myContact);

  return (
    <div className="min-w-0 rounded-2xl border border-td-line bg-white p-4">
      <div className="flex min-w-0 items-start gap-3">
        {inv.photo_url ? (
          <img loading="lazy" decoding="async" src={inv.photo_url} alt="" className="h-12 w-12 shrink-0 rounded-full object-cover" />
        ) : (
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-td-soft font-semibold text-td-dark">
            {(inv.full_name || "?").charAt(0)}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <Link to="/people/$userId" params={{ userId: inv.other_user_id }} className="font-semibold text-td-dark hover:underline">
            {inv.full_name}
          </Link>
          <p className="text-xs text-td-muted">
            {[inv.city, (inv.direction === "incoming" ? "пригласил(а) " : "приглашение от ") + fmtDate(inv.created_at)].filter(Boolean).join(" · ")}
          </p>
          {inv.message && <p className="mt-2 break-words text-sm italic text-td-dark">«{inv.message}»</p>}
          {!canRespond && (
            <p className="mt-2 text-sm font-medium text-td-teal">{STATUS_LABEL[inv.status] ?? inv.status}</p>
          )}
          {inv.reply_message && (
            <p className="mt-1 break-words text-sm text-td-muted">
              {inv.direction === "incoming" ? "Твой ответ" : "Ответ"}: «{inv.reply_message}»
            </p>
          )}
        </div>
      </div>

      {canRespond && choice === null && (
        <div className="mt-3 flex flex-wrap gap-2">
          <Button className="bg-td-teal hover:bg-td-teal/90" onClick={() => setChoice(true)}>Давай встретимся</Button>
          <Button variant="outline" onClick={() => setChoice(false)}>Не сейчас</Button>
        </div>
      )}
      {canRespond && choice !== null && (
        <div className="mt-3 space-y-2">
          {choice && !meHasContact && myUserId && (
            <div className="space-y-2">
              <p className="text-xs text-td-muted">
                Добавь телеграм или ссылку, чтобы вам было где договориться
              </p>
              <ContactSaver userId={myUserId} />
            </div>
          )}
          <Textarea
            rows={2}
            maxLength={200}
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder={choice ? "Например: давай в четверг после обеда" : "Можно пару слов (необязательно)"}
          />
          <div className="flex flex-wrap gap-2">
            <Button
              className={choice ? "bg-td-teal hover:bg-td-teal/90" : ""}
              variant={choice ? "default" : "outline"}
              disabled={respond.isPending || (choice === true && !meHasContact)}
              onClick={() => respond.mutate(choice)}
            >
              {choice ? "Отправить согласие" : "Отправить отказ"}
            </Button>
            <Button variant="ghost" onClick={() => setChoice(null)} disabled={respond.isPending}>Назад</Button>
          </div>
        </div>
      )}

      {agreed && (
        <div className="mt-3 space-y-2">
          {partnerHasContact ? (
            <>
              <ContactButtons telegram={partnerTelegram} links={partnerLinks} />
              <p className="text-xs text-td-muted">Дальше договаривайтесь, где вам удобно</p>
            </>
          ) : (
            <p className="text-xs text-td-muted">
              У этого резидента пока нет контактов в профиле
              {!meHasContact && " — добавь свой, чтобы вам было где договориться"}
            </p>
          )}
          {!meHasContact && myUserId && <ContactSaver userId={myUserId} />}
        </div>
      )}
    </div>
  );
}
